import { useMemo, useState } from "react";
import { CircularProgress } from "@mui/material";
import moment from "moment";

import PrimaryButton from "../../../Components/Buttons/primary-button";

import { useDeals } from "../../../CustomHooks/Queries";

import { appendISOTime, formatCurrency, formatDate } from "../../../Utils/helpers";
import { modalVariants, nilYearLongMonthDateFormat, longMonthDateFormat } from "../../../Utils/constants";

import { BankingOutline, Card } from "../../../Assets/Icons";

import "./transfer.scss";

export default function renderAuthorizeTransfer(modalContext, props) {
  const { updateModalComponent, pushModalStack } = modalContext;

  pushModalStack(() => renderAuthorizeTransfer(modalContext, props));
  updateModalComponent(() => <AuthorizeTransfer {...props} />);
  setAuthorizeTransferModalProps(modalContext);
}

export const setAuthorizeTransferModalProps = (modalContext) => {
  const { setModalVariant, setModalTitle, setModalClassName } = modalContext;
  setModalVariant(modalVariants.flexDialog);
  setModalClassName("authorize-transfer-modal hide-back-button align-title-left");
  setModalTitle("Authorize this transaction");
};

export const AuthorizeTransfer = (props) => {
  const {
    selectedAccount,
    selectedAstraCard,
    clearingTransactionDate,
    amount,
    isRecurringDeposit = false,
    recurringDepositPreferences,
    AuthorizeCallback,
    isCashOut = false,
  } = props;

  const [isLoading, setIsLoading] = useState(false);
  const [achAgreed, setAchAgreed] = useState(false);

  const { data: deals } = useDeals();

  const formattedCurrentDate = useMemo(() => {
    return formatDate(new Date(), longMonthDateFormat);
  }, []);

  const formattedArrivalDate = useMemo(() => {
    if (selectedAstraCard) return "in 30 minutes";
    else return `within ${clearingTransactionDate}`;
  }, [clearingTransactionDate, selectedAstraCard]);

  const formattedRecurringStartDate = useMemo(() => {
    return formatDate(appendISOTime(recurringDepositPreferences?.start_date), nilYearLongMonthDateFormat);
  }, [recurringDepositPreferences?.start_date]);

  const recurringDayOfTheWeek = useMemo(() => {
    if (!!recurringDepositPreferences?.start_date) {
      const date = moment(recurringDepositPreferences.start_date);
      return date.format("dddd");
    }
    return null;
  }, [recurringDepositPreferences?.start_date]);

  const debitDateLabel = useMemo(() => {
    if (isRecurringDeposit) return formattedRecurringStartDate;
    return formattedCurrentDate;
  }, [isRecurringDeposit, formattedRecurringStartDate, formattedCurrentDate]);

  const feeLabel = useMemo(() => {
    const feePercentage = deals?.data?.data?.currentDeal?.INSTANT_TRANSFER_OUT_OF_STAKE_FEE_RATE ?? 0.0175;

    if (!!selectedAstraCard) {
      return formatCurrency(amount * feePercentage);
    } else {
      return "Free";
    }
  }, [deals, amount, selectedAstraCard]);

  const isFree = feeLabel === "Free";

  const accountName = useMemo(() => {
    if (selectedAstraCard) return `${selectedAstraCard.card_company} (...${selectedAstraCard.last_four_digits})`;
    if (selectedAccount) return `${selectedAccount.name} (...${selectedAccount.mask})`;
    return "Bank Account";
  }, [selectedAstraCard, selectedAccount]);

  const fromName = isCashOut ? "Stake" : accountName;
  const toName = isCashOut ? accountName : "Stake";

  const paymentMethodLabel = selectedAstraCard ? "Debit Card" : "ACH / Checking Account";

  const fromIcon = isCashOut ? <BankingOutline /> : (selectedAstraCard ? <Card /> : <BankingOutline />);
  const toIcon = isCashOut ? (selectedAstraCard ? <Card /> : <BankingOutline />) : <BankingOutline />;

  const disclaimerText = useMemo(() => {
    if (selectedAstraCard) return "Transfer speed depends on your bank and could take up to 30 minutes.";
    if (isRecurringDeposit) return "Transfers take one business day to process. Transfers set up on a weekend, or holiday, will occur on the next business day.";
    return "2-3 business days. Transactions made after 3:00 PM ET or on a weekend or holiday will be processed the next business day.";
  }, [selectedAstraCard, isRecurringDeposit]);

  const handleAuthorize = () => {
    setIsLoading(true);
    AuthorizeCallback();
  };

  return (
    <div className="modal-dialog-content authorize-transfer-v2">
      <div className="at-amount-hero">
        <span className="at-amount-label">Amount</span>
        <span className="at-amount">{formatCurrency(amount)}</span>
        <span className="at-amount-desc">Arrives {formattedArrivalDate}</span>
      </div>
      <div className="at-transfer-card">
        <div className="at-card-row at-card-row-border">
          <span className="at-card-label">From</span>
          <div className="at-card-entity">
            <span className="at-card-value">{fromName}</span>
            <span className="at-card-icon">{fromIcon}</span>
          </div>
        </div>
        <div className="at-card-row">
          <span className="at-card-label">To</span>
          <div className="at-card-entity">
            <span className="at-card-value">{toName}</span>
            <span className="at-card-icon">{toIcon}</span>
          </div>
        </div>
      </div>
      <div className="at-details-section">
        <div className="at-detail-row at-detail-row-border">
          <span className="at-detail-label">Payment Method</span>
          <span className="at-detail-value">{paymentMethodLabel}</span>
        </div>
        {formattedArrivalDate && (
          <div className="at-detail-row at-detail-row-border">
            <span className="at-detail-label">Expected Arrival</span>
            <span className="at-detail-value">{formattedArrivalDate}</span>
          </div>
        )}
        {isRecurringDeposit && (
          <div className="at-detail-row at-detail-row-border">
            <span className="at-detail-label">Frequency</span>
            <span className="at-detail-value">
              {recurringDepositPreferences?.frequency_type}
              {recurringDepositPreferences?.frequency_type !== "Monthly" && `, ${recurringDayOfTheWeek}`}
            </span>
          </div>
        )}
        <div className="at-detail-row">
          <span className="at-detail-label">Fees</span>
          <span className={`at-detail-value${isFree ? " at-fee-free" : ""}`}>{feeLabel}</span>
        </div>
      </div>
      <div className="at-footer">
        <p className="at-disclaimer">*{disclaimerText}</p>
        <label className="ach-authorization-checkbox" onClick={(e) => e.target.closest("a") && e.stopPropagation()}>
          <input type="checkbox" checked={achAgreed} onChange={(e) => setAchAgreed(e.target.checked)} />
          <span className="ach-authorization-text">
            By selecting <strong>&ldquo;Authorize,&rdquo;</strong> you instruct Stake to process a payment or funds
            transfer using the payment method you have selected &mdash; including bank account via ACH or debit card
            &mdash; based on the details you provided. You also confirm that you have read and agree to the terms of our{" "}
            <a
              href="https://stake-platform-s3.s3.us-east-1.amazonaws.com/terms/ach-authorization-agreement.pdf"
              target="_blank"
              rel="noreferrer"
              className="ach-authorization-link"
            >
              ACH Authorization Agreement
            </a>
            , you are the authorized owner of the selected account, and the amount, destination and timing entered in
            the application reflect your instructions.
          </span>
        </label>
        <PrimaryButton onClick={handleAuthorize} disabled={isLoading || !achAgreed}>
          Authorize {isLoading && <CircularProgress />}
        </PrimaryButton>
      </div>
    </div>
  );
};
